// types/storeManager.types.ts
//
// Store delegation model. A store account can grant a staff member
// permission to act on its behalf (create tournaments, manage
// series, etc.) without sharing the store login. The staff member
// keeps their own account; their "store manager" privileges
// activate only when they're acting under that store.
//
// Three records make the model work:
//
//   /stores/{storeId}/managers/{uid}            — store-side roster
//   /users/{uid}/storeManagerships/{storeId}    — user-side roster
//   /storeManagerInvites/{token}                — single-use invite
//
// The user-side and store-side roster records are kept in sync by
// the storeManagerService — every accept / revoke / remove writes
// both atomically.

/**
 * Per-store-per-user permission flags. Starts intentionally
 * narrow (3 toggles); extra fields land here as the delegation
 * surface grows. All default false — assignments are explicit
 * grants, never inherited.
 */
export interface StoreManagerPermissions {
  /** Create / edit / delete tournaments owned by this store,
   *  including managing the staff assignments on those events. */
  canCreateAndEditTournaments: boolean;
  /** Create / edit tournament series for this store, attach /
   *  detach member tournaments, manage the series staff pool. */
  canManageSeries: boolean;
  /** Invite, revoke, and remove OTHER store managers under this
   *  store. Recursive — default off; only granted when a store
   *  needs a co-owner. */
  canManageOtherProxies: boolean;
}

/**
 * Record written to /stores/{storeId}/managers/{uid}. Lets the
 * store's manage-proxies UI list its delegates without having
 * to scan /users.
 */
export interface StoreManagerEntry {
  uid: string;
  /** Display name cached at assignment time so the roster can
   *  render without an extra /users lookup. Updated when the
   *  user updates their profile name. */
  name: string;
  email?: string;
  permissions: StoreManagerPermissions;
  assignedAt: number;
  assignedBy: string;
}

/**
 * Record written to /users/{uid}/storeManagerships/{storeId}.
 * Lets the user-side know "I can act for these stores" without
 * scanning /stores. Mirrors the permission set so route gates and
 * permission hooks can answer locally.
 */
export interface StoreManagership {
  storeId: string;
  /** Cached store name + the user's display title. */
  storeName: string;
  /** Auth uid of the store account that owns the delegated store.
   *  Needed so the proxy can attribute writes (tournament.ownerUid,
   *  staffAssignments entries) to the actual store user — without
   *  this, the store dashboard misses tournaments the proxy
   *  created because ownerUid would point at the storeId path key
   *  instead of the store user's auth uid. */
  storeOwnerUid: string;
  permissions: StoreManagerPermissions;
  assignedAt: number;
}

/**
 * Single-use invite token. Written by the store owner when
 * generating an invite link; consumed atomically by the claim
 * flow. Permissions are NOT carried by the invite — they're set
 * after the staff member accepts (the user picked the 'set
 * permissions after claim' shape so the invite UI stays simple).
 */
export interface StoreManagerInvite {
  /** Random opaque token used as the path key. Generated client-
   *  side via crypto.randomUUID() for read-only display in the
   *  invite link. */
  token: string;
  storeId: string;
  storeName: string;
  /** The store account's auth uid — same as createdBy in normal
   *  use (the store owner creates their own invites). Copied
   *  forward into the StoreManagership on claim so the proxy
   *  knows which uid to attribute store-side writes to. */
  storeOwnerUid: string;
  createdBy: string;
  createdAt: number;
  /** UNIX millis. Default 24h after creation. Past this point the
   *  claim flow refuses. */
  expiresAt: number;
  /** Set when a user accepts. Once non-null, the claim flow
   *  rejects any further attempts ('already used'). */
  consumedAt: number | null;
  consumedByUid: string | null;
}

/**
 * Friendly preset shapes for the permission picker on the store-
 * managers page. 'Custom' isn't a preset — it's the fallback when
 * the operator wants to tick boxes individually.
 */
export type StoreManagerPreset = 'manager' | 'co-owner';

export const STORE_MANAGER_PRESETS: Record<
  StoreManagerPreset,
  { label: string; description: string; permissions: StoreManagerPermissions }
> = {
  manager: {
    label: 'Manager',
    description:
      'Can run tournaments and series for this store. Cannot invite or remove other managers.',
    permissions: {
      canCreateAndEditTournaments: true,
      canManageSeries: true,
      canManageOtherProxies: false,
    },
  },
  'co-owner': {
    label: 'Co-owner',
    description:
      'Everything a manager can do, plus invite and remove other managers under this store.',
    permissions: {
      canCreateAndEditTournaments: true,
      canManageSeries: true,
      canManageOtherProxies: true,
    },
  },
};

/** Convenience: an empty permission set. New invites + new claim
 *  defaults start here. */
export const EMPTY_PERMISSIONS: StoreManagerPermissions = {
  canCreateAndEditTournaments: false,
  canManageSeries: false,
  canManageOtherProxies: false,
};

/** Default invite TTL — 24 hours. */
export const INVITE_TTL_MS = 24 * 60 * 60 * 1000;
